<template>
  <v-card
    :class="{
      error,
      'text-center': small,
      'justify-center': small,
      'justify-between': !small && selected,
      'remove-border': noBorder,
    }"
    class="d-flex"
    :color="bgColor"
    :disabled="disabled"
    :max-width="maxWidth"
    :ripple="!unripple"
    :style="{ color: this.selected && this.small ? 'white' : 'black' }"
    @click="onCardClicked"
  >
    <div class="label p-2 text-sm">
      {{ text }}
      <div
          v-if="initials"
          class="text-xs font-bold text-center d-inline pa-1 rounded"
          :style="{
          backgroundColor: initialsBackgroundColor,
          color: $filters.foregroundColor(initialsBackgroundColor),
        }"
      >
        {{ initials }}
      </div>
      <span
          v-if="duration"
          class="pa-1 text-xs font-weight-light"
      >
        {{ this.durationFormat }}
      </span>
    </div>
    <div
      v-if="!small && selected"
      class="action-icon"
    >
      <v-btn
          disabled
          icon="mdi-checkbox-marked-circle-outline"
          variant="text"
          density="compact"
      />
    </div>
    <div
      v-if="error"
      class="error-indicator"
    >
      <span>No practitioners</span>
      <v-icon
        :size="18"
        color="red"
      >{{ icons.error }}</v-icon>
    </div>
    <div
      v-if="remove && !error"
      class="action-icon"
    >
      <v-btn
        @mousedown.stop
        @touchstart.stop
        icon="mdi-delete"
        variant="text"
        density="compact"
        @click.stop="onRemoveClicked"
      />
    </div>
    <div
      v-if="context && !error"
      class="action-icon"
    >
      <v-btn
        icon="mdi-dots-horizontal"
        variant="text"
        density="compact"
        @mousedown.stop
        @touchstart.stop
        @click.stop="onContextClicked"
      />
    </div>
  </v-card>
</template>

<script>
import { defineComponent } from 'vue';
import {
  mdiAlert,
  mdiCheckboxMarkedCircleOutline,
  mdiDelete,
  mdiDotsHorizontal
} from '@mdi/js';
import randomColor from "randomcolor";

export default defineComponent({
  name: 'TextCard',
  emits: ['click', 'click:card-error', 'click:card-default', 'click:context', 'click:remove'],
  props: {
    context: { type: Boolean, default: false },
    initials: { type: String, required: false },
    initialsColor: { type: String, required: false },
    duration: { type: Number, required: false },
    disabled: { type: Boolean, default: false },
    error: { type: String, default: '' },
    remove: { type: Boolean, default: false },
    selected: { type: Boolean, default: false },
    small: { type: Boolean, default: false },
    text: { type: String, required: true },
    unripple: { type: Boolean, default: false },
    noBorder: { type: Boolean, default: false }
  },
  data() {
    return {
      icons: {
        context: mdiDotsHorizontal,
        remove: mdiDelete,
        error: mdiAlert,
        selected: mdiCheckboxMarkedCircleOutline,
      }
    }
  },
  computed: {
    bgColor() {
      if (this.small && this.selected) {
        return 'primary';
      } else if (this.small) {
        return 'gray-darken-1';
      }
      return 'gray-lighten-1';
    },
    maxWidth() {
      return this.small ? 112 : 360;
    },
    initialsBackgroundColor() { return this.initialsColor || randomColor() },
    durationFormat() {
      const minutes = this.duration / 60
      const seconds = this.duration % 60

      return minutes > 0 ? (parseInt(minutes) + " min " + (seconds > 0 ? seconds + " sec" : '')) : (seconds + " sec")
    }
  },
  methods: {
    onCardClicked(e) {
      e.target.blur()
      this.$emit('click')
      if (this.error) this.$emit('click:card-error')
      else this.$emit('click:card-default')
    },
    onContextClicked(e) {
      e.target.blur()
      this.$emit('click:context')
    },
    onRemoveClicked(e) {
      e.target.blur()
      this.$emit('click:remove')
    },
  }
})
</script>

<style scoped>
.v-application .v-card.error {
  border: 2px solid var(--v-red-base) !important;
}

.action-icon {
  display: flex;
  align-items: center;
  padding-right: 5px;
}

.label {
  display: flex;
  gap: 5px;
  align-items: center;
  flex: 1;
  user-select: none;
}

.error-indicator {
  color: var(--v-red-base);
  font-weight: bold;
}

.error-indicator > * + * {
  @apply ml-2
}

.remove-border {
  box-shadow: unset !important;
}
</style>
